/**
 * Keeps the landing page on the build-time image files (public/snapshot/*,
 * written by scripts/snapshotConfig.js) once the live config arrives.
 *
 * WHY: on a first visit the landing page renders from the build snapshot,
 * whose images are real files served by Hosting. The live config then brings
 * the same images again as base64 data URLs. Swapping one for the other makes
 * every photo re-decode and blink, for no visual change. So wherever the live
 * image is the one that was exported, the static file is kept. An image an
 * admin changed after the deploy does not match, and shows the live copy.
 */

/**
 * Cheap identity for an image data URL — its length plus both ends. MUST stay
 * identical to fingerprint() in scripts/snapshotConfig.js, which computed the
 * values this is compared against.
 *
 * @param {string} dataUrl
 * @returns {string}
 */
export const fingerprintImage = (dataUrl) => `${dataUrl.length}:${dataUrl.slice(0, 64)}:${dataUrl.slice(-64)}`;

/**
 * Reads a dotted path ("landingImages.0.url") from an object.
 *
 * @param {object} obj
 * @param {string[]} keys
 * @returns {unknown}
 */
const getAtPath = (obj, keys) => keys.reduce((node, key) => (node == null ? undefined : node[key]), obj);

/**
 * Returns a copy of `obj` with the value at `keys` replaced, cloning only the
 * objects/arrays along that path (everything else is shared).
 *
 * @param {object|Array} obj
 * @param {string[]} keys
 * @param {unknown} value
 * @returns {object|Array}
 */
const setAtPath = (obj, [key, ...rest], value) => {
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  copy[key] = rest.length === 0 ? value : setAtPath(obj[key], rest, value);
  return copy;
};

/**
 * Points every landing image that is unchanged since the deploy at its static
 * file.
 *
 * @param {object|null} config - a live or cached config (images as data URLs).
 * @param {Object<string,{fingerprint:string,url:string}>} snapshotImages -
 *   the build snapshot's `images`: live-config path -> the exported image's
 *   fingerprint and static file URL.
 * @returns {object|null} `config` itself when nothing matched, else a copy.
 */
export const applySnapshotImages = (config, snapshotImages) => {
  if (!config) return config;
  return Object.entries(snapshotImages || {}).reduce((result, [configPath, entry]) => {
    if (!entry?.url || !entry?.fingerprint) return result;
    const keys = configPath.split('.');
    const live = getAtPath(result, keys);
    if (typeof live !== 'string') return result;
    return fingerprintImage(live) === entry.fingerprint ? setAtPath(result, keys, entry.url) : result;
  }, config);
};
